/**
 * Barcode Label Print (Zebra / TSC / generic thermal).
 *
 * Designed for the issues users hit with browser-driven thermal printing:
 *   - Blank labels: empty trailing nodes after the last <div> are removed,
 *     no whitespace between labels so the renderer can't insert phantom rows.
 *   - Empty pages: page-break-after applied to all labels EXCEPT the last so
 *     the printer doesn't feed an extra blank label at the end.
 *   - Wrong size: @page size is set to the exact label dimensions and the
 *     body is sized identically; box-sizing: border-box on every label keeps
 *     borders/padding from pushing content off-page.
 *   - Overflow garbage: every label has overflow: hidden so an overlong item
 *     name is clipped rather than spilling into the next label.
 *   - Lingering print queue: window.onafterprint closes the helper window so
 *     the printer driver releases the job promptly.
 *
 * Cashier must still confirm in the browser print dialog:
 *   - "Scale" = 100% (NOT "fit to page")
 *   - "Margins" = None
 *   - Correct label-roll size in the printer driver (Zebra Setup Utility /
 *     TSC label tool)
 * The print window's header surfaces these as a one-line hint.
 */

import { formatCurrency } from './formatters';

export const DEFAULT_LABEL = {
  // Layout
  paperPreset: '38x28mm',  // '38x28mm' | '2x1in' | '2x1in-doubleup' | 'custom'
  widthMm: 38,
  heightMm: 28,
  columns: 1,              // 1 for single, 2 for double-up
  columnGapMm: 2,
  paddingMm: 2,

  // Field toggles
  showCompanyName: false,
  showItemName: true,
  showItemCode: true,
  showSalePrice: true,
  showMrp: false,
  showBarcode: true,
  showBarcodeText: true,

  // Font sizes (pt)
  companyFontPt: 7,
  itemNameFontPt: 9,
  itemCodeFontPt: 7,
  priceFontPt: 11,
  barcodeTextFontPt: 7,

  // Barcode visual
  barcodeHeightMm: 10,
  barcodeFormat: 'CODE128', // CODE128, EAN13, etc.

  // Misc
  boldPrice: true,
  currencyPrefix: 'Rs ',
  priceLabel: '',           // optional like "Price:"
  mrpLabel: 'MRP'
};

const PRESETS = {
  '38x28mm':         { widthMm: 38, heightMm: 28, columns: 1, columnGapMm: 0 },
  '2x1in':           { widthMm: 50.8, heightMm: 25.4, columns: 1, columnGapMm: 0 },
  '2x1in-doubleup':  { widthMm: 50.8, heightMm: 25.4, columns: 2, columnGapMm: 3 }, // 3mm gutter
};

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

/**
 * Expand a [{ item, qty }] array into individual labels.
 * Defensive: drops zero/blank rows.
 */
const expandLabels = (rows) => {
  const out = [];
  for (const r of rows || []) {
    const qty = Math.max(0, Math.floor(Number(r.qty) || 0));
    for (let i = 0; i < qty; i++) out.push(r.item);
  }
  return out;
};

/**
 * Resolve a preset name down to dimensions, allowing 'custom' to use the raw fields.
 */
const resolveDims = (cfg) => {
  if (cfg.paperPreset && cfg.paperPreset !== 'custom' && PRESETS[cfg.paperPreset]) {
    return { ...PRESETS[cfg.paperPreset] };
  }
  return {
    widthMm: Number(cfg.widthMm) || 38,
    heightMm: Number(cfg.heightMm) || 28,
    columns: Number(cfg.columns) || 1,
    columnGapMm: Number(cfg.columnGapMm) || 0,
  };
};

/**
 * Print labels.
 *
 * @param rows           [{ item: {barcode, itemName, itemCode, salePrice, mrp}, qty }]
 * @param activeCompany  company doc (for company name)
 * @param settings       barcode label config (from documentFormats.barcodeLabel)
 */
export const printBarcodeLabels = (rows, activeCompany, settings = null) => {
  const cfg = { ...DEFAULT_LABEL, ...(settings || {}) };
  const dims = resolveDims(cfg);
  const totalRows = expandLabels(rows);
  if (totalRows.length === 0) {
    alert('No labels to print — set a quantity for at least one item.');
    return;
  }
  // Hard ceiling so a typo (e.g. qty 100000) doesn't lock the browser.
  const MAX_LABELS = 2000;
  const labels = totalRows.slice(0, MAX_LABELS);

  const pageWidthMm = dims.widthMm * dims.columns + dims.columnGapMm * (dims.columns - 1);
  const pageHeightMm = dims.heightMm;

  // For double-up: group labels into rows of `columns`. We still page-break
  // after each row, so each printed "page" is one label row.
  const groups = [];
  for (let i = 0; i < labels.length; i += dims.columns) {
    groups.push(labels.slice(i, i + dims.columns));
  }

  const buildOneLabel = (item) => {
    if (!item) return `<div class="label empty"></div>`;
    const companyLine = cfg.showCompanyName && activeCompany?.companyName
      ? `<div class="company">${esc(activeCompany.companyName)}</div>` : '';
    const nameLine = cfg.showItemName && item.itemName
      ? `<div class="item-name">${esc(item.itemName)}</div>` : '';
    const codeLine = cfg.showItemCode && item.itemCode
      ? `<div class="item-code">${esc(item.itemCode)}</div>` : '';
    const priceLine = cfg.showSalePrice && (item.salePrice != null)
      ? `<div class="price">${cfg.priceLabel ? esc(cfg.priceLabel) + ' ' : ''}${esc(cfg.currencyPrefix || '')}${formatCurrency(Number(item.salePrice) || 0)}</div>`
      : '';
    const mrpLine = cfg.showMrp && (item.mrp != null && Number(item.mrp) > 0)
      ? `<div class="mrp">${esc(cfg.mrpLabel || 'MRP')} ${esc(cfg.currencyPrefix || '')}${formatCurrency(Number(item.mrp))}</div>`
      : '';

    const code = item.barcode || '';
    const barcodeBlock = cfg.showBarcode && code
      ? `
        <div class="barcode">
          <svg class="bc"></svg>
          ${cfg.showBarcodeText ? `<div class="bc-text">${esc(code)}</div>` : ''}
        </div>
      `
      : '';

    return `
      <div class="label" data-code="${esc(code)}">
        ${companyLine}
        ${nameLine}
        ${codeLine}
        ${priceLine}
        ${mrpLine}
        ${barcodeBlock}
      </div>
    `;
  };

  const buildGroupRow = (grp, isLast) => {
    // For columns=1 each row is one label. For columns=2 each row is two labels
    // side-by-side. The row gets the page-break — never the inner labels.
    const inner = grp.map(buildOneLabel).join('');
    // Pad short final row with empty placeholders so the layout stays grid-aligned.
    const padCount = dims.columns - grp.length;
    const pads = padCount > 0 ? Array.from({ length: padCount }).map(() => `<div class="label empty"></div>`).join('') : '';
    return `<div class="row ${isLast ? 'last' : ''}">${inner}${pads}</div>`;
  };

  const body = groups.map((g, i) => buildGroupRow(g, i === groups.length - 1)).join('');

  // Render each barcode with JsBarcode after page load. Falls through silently
  // on CDN failure — at least the receipt # text is visible underneath.
  const barcodeScript = cfg.showBarcode ? `
    var s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js';
    s.onload = function() {
      document.querySelectorAll('.label').forEach(function(el){
        var code = el.getAttribute('data-code');
        var svg = el.querySelector('svg.bc');
        if (code && svg) {
          try {
            JsBarcode(svg, code, {
              format: ${JSON.stringify(cfg.barcodeFormat || 'CODE128')},
              width: 1.4,
              height: ${Number(cfg.barcodeHeightMm) || 10} * 3.78,
              displayValue: false,
              margin: 0
            });
          } catch(e) {}
        }
      });
      setTimeout(printNow, 30);
    };
    s.onerror = function(){ setTimeout(printNow, 0); };
    document.head.appendChild(s);
  ` : 'setTimeout(printNow, 0);';

  const html = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<title>Barcode Labels (${labels.length})</title>
<style>
  /* Critical: page size matches label row exactly so the printer feeds one
     label per "page". margin:0 prevents browser-default 6mm margins which
     are the #1 cause of blank labels / shifted prints on thermal rolls. */
  @page { size: ${pageWidthMm}mm ${pageHeightMm}mm; margin: 0; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    background: #fff;
  }
  .row {
    width: ${pageWidthMm}mm;
    height: ${pageHeightMm}mm;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    /* Page-break ON every row except the last to prevent a trailing blank page. */
    page-break-after: always;
    break-after: page;
  }
  .row.last { page-break-after: auto; break-after: auto; }
  .label {
    width: ${dims.widthMm}mm;
    height: ${dims.heightMm}mm;
    box-sizing: border-box;
    padding: ${Number(cfg.paddingMm) || 2}mm;
    overflow: hidden;             /* clip overflowing content; never spill */
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: space-between;
    line-height: 1.15;
  }
  .label + .label { margin-left: ${dims.columnGapMm}mm; }
  .label.empty { background: transparent; }

  .company {
    text-align: center;
    font-size: ${cfg.companyFontPt}pt;
    font-weight: 700;
    text-transform: uppercase;
    line-height: 1.1;
  }
  .item-name {
    text-align: center;
    font-size: ${cfg.itemNameFontPt}pt;
    font-weight: 600;
    line-height: 1.1;
    /* clamp to 2 lines so long names don't push out the price/barcode */
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .item-code {
    text-align: center;
    font-size: ${cfg.itemCodeFontPt}pt;
    color: #333;
  }
  .price {
    text-align: center;
    font-size: ${cfg.priceFontPt}pt;
    ${cfg.boldPrice ? 'font-weight: 900;' : ''}
  }
  .mrp {
    text-align: center;
    font-size: ${Math.max(6, cfg.priceFontPt - 2)}pt;
    color: #555;
  }
  .barcode {
    width: 100%;
    text-align: center;
    margin-top: 1mm;
  }
  .barcode svg.bc {
    display: block;
    margin: 0 auto;
    width: 92%;
    max-width: 100%;
    height: ${Number(cfg.barcodeHeightMm) || 10}mm;
  }
  .bc-text {
    font-family: 'Courier New', monospace;
    font-size: ${cfg.barcodeTextFontPt}pt;
    letter-spacing: 0.5px;
    margin-top: 0.5mm;
  }

  /* Screen-only banner: hidden when printing. */
  .hint {
    background: #FFF3CD;
    color: #5B4400;
    padding: 6px 10px;
    font-size: 11px;
    line-height: 1.35;
  }
  .hint b { color: #B45309; }
  @media print { .hint { display: none; } }
</style>
</head><body>
<div class="hint">
  <b>Printer check:</b> Scale = <b>100%</b> (not "Fit to page") · Margins = <b>None</b> ·
  Driver paper size = <b>${pageWidthMm} × ${pageHeightMm} mm</b> · ${labels.length} label${labels.length !== 1 ? 's' : ''}.
</div>
${body}<script>
function printNow(){ try { window.print(); } catch(e) {} }
window.onafterprint = function(){ try { window.close(); } catch(e){} };
window.onload = function(){ ${barcodeScript} };
</script></body></html>`;

  // Width small enough to suit the typical 38mm label preview but tall enough
  // to scroll a few labels at once before the print dialog opens.
  const winW = Math.max(280, Math.min(540, Math.round(pageWidthMm * 4)));
  const win = window.open('', '_blank', `width=${winW},height=700`);
  if (win) {
    win.document.write(html);
    win.document.close();
  } else {
    alert('Popup was blocked. Allow pop-ups for this site to print barcode labels.');
  }
};

export default { printBarcodeLabels, DEFAULT_LABEL };
